import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../AppIcon';
import Image from '../AppImage';

function Card({
  variant = 'default',
  title,
  subtitle,
  description,
  image,
  icon,
  iconColor,
  footer,
  actions,
  onClick,
  className = '',
  children,
  delay = 0,
  ...props
}) {
  // Modern gradient backgrounds for different variants
  const gradientBgs = {
    blue: 'from-blue-500/10 to-indigo-500/10',
    purple: 'from-purple-500/10 to-pink-500/10',
    green: 'from-green-500/10 to-emerald-500/10',
    orange: 'from-orange-500/10 to-red-500/10',
  };

  const baseClasses = 'rounded-2xl border border-slate-200/60 bg-white backdrop-blur-sm overflow-hidden';

  const variantClasses = {
    default: '',
    service: 'group',
    technology: 'group',
    testimonial: 'p-8 3xl:p-10',
    feature: 'p-8 3xl:p-10',
    interactive: 'group cursor-pointer',
  };

  const cardClasses = [
    baseClasses,
    variantClasses[variant],
    onClick ? 'cursor-pointer' : '',
    className
  ].join(' ');

  // Enhanced animation variants
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 60,
      scale: 0.92
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: delay,
        ease: [0.22, 1, 0.36, 1], // Custom easing for smooth motion
        when: "beforeChildren",
        staggerChildren: 0.12
      }
    }
  };

  // Enhanced hover with subtle lift
  const hoverVariants = {
    scale: 1.02,
    y: -12,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 25
    }
  };

  // Icon animation with glow effect
  const iconVariants = {
    hidden: { scale: 0, rotate: -180, opacity: 0 },
    visible: {
      scale: 1,
      rotate: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 260,
        damping: 20,
        delay: delay + 0.1
      }
    }
  };

  // Content reveal animation
  const contentVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  // Service Card - Modern Design
  if (variant === 'service') {
    return (
      <motion.div
        className={`${cardClasses} relative flex flex-col shadow-lg shadow-slate-200/50 hover:shadow-2xl hover:shadow-primary-500/20`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={hoverVariants}
        onClick={onClick}
        {...props}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {image && (
          <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
            <motion.div
              className="w-full h-full"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Image
                src={image}
                alt={title}
                className="w-full h-full object-cover"
              />
            </motion.div>
            {/* Image overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        )}

        <motion.div
          className="relative p-6 sm:p-8 3xl:p-10 flex-1 flex flex-col"
          variants={contentVariants}
        >
          {icon && (
            <motion.div
              className={`mb-5 3xl:mb-6 inline-flex items-center justify-center w-14 h-14 3xl:w-16 3xl:h-16 rounded-xl bg-gradient-to-br ${iconColor || 'from-primary-500 to-primary-600'} shadow-lg shadow-primary-500/30 group-hover:shadow-primary-500/50 transition-shadow duration-300`}
              variants={iconVariants}
              whileHover={{
                scale: 1.1,
                rotate: [0, -10, 10, -10, 0],
                transition: { duration: 0.5 }
              }}
            >
              <Icon name={icon} size={28} className="text-white 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 5xl:w-12 5xl:h-12 6xl:w-14 6xl:h-14" />
            </motion.div>
          )}

          {title && (
            <motion.h3
              className="text-lg sm:text-xl md:text-2xl 3xl:text-3xl font-bold text-slate-900 mb-3 group-hover:text-primary-600 transition-colors duration-300"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: delay + 0.2 }}
            >
              {title}
            </motion.h3>
          )}

          {subtitle && (
            <p className="text-sm 3xl:text-base font-semibold text-primary-600 mb-3 uppercase tracking-wide">
              {subtitle}
            </p>
          )}

          {description && (
            <p className="text-sm sm:text-base text-slate-600 3xl:text-lg leading-relaxed mb-6">
              {description}
            </p>
          )}

          {children}

          {actions && (
            <motion.div
              className="mt-auto pt-6 flex items-center justify-between border-t border-slate-100"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: delay + 0.35 }}
            >
              {actions}
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    );
  }

  // Technology Card - Ultra Modern Design
  if (variant === 'technology') {
    return (
      <motion.div
        className={`${cardClasses} relative p-6 3xl:p-8 shadow-md hover:shadow-xl shadow-slate-200/60 hover:shadow-primary-500/20`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={{
          scale: 1.03,
          y: -8,
          transition: {
            type: "spring",
            stiffness: 400,
            damping: 25
          }
        }}
        onClick={onClick}
        {...props}
      >
        {/* Animated gradient background */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-primary-500/5 via-purple-500/5 to-pink-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          whileHover={{
            background: [
              "linear-gradient(135deg, rgba(59, 130, 246, 0.05), rgba(147, 51, 234, 0.05), rgba(236, 72, 153, 0.05))",
              "linear-gradient(135deg, rgba(147, 51, 234, 0.05), rgba(236, 72, 153, 0.05), rgba(59, 130, 246, 0.05))",
              "linear-gradient(135deg, rgba(236, 72, 153, 0.05), rgba(59, 130, 246, 0.05), rgba(147, 51, 234, 0.05))",
            ],
            transition: { duration: 3, repeat: Infinity }
          }}
        />

        <div className="relative flex items-start space-x-5">
          {icon && (
            <motion.div
              className="flex-shrink-0 w-16 h-16 3xl:w-20 3xl:h-20 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/30 group-hover:shadow-primary-500/50 transition-all duration-300"
              variants={iconVariants}
              whileHover={{
                scale: 1.15,
                rotate: [0, -5, 5, -5, 0],
                transition: { duration: 0.6 }
              }}
            >
              <Icon name={icon} size={32} className="text-white 3xl:w-10 3xl:h-10" />
            </motion.div>
          )}

          {image && (
            <motion.div
              className="flex-shrink-0 w-16 h-16 3xl:w-20 3xl:h-20 rounded-xl overflow-hidden bg-white shadow-lg p-2 group-hover:shadow-xl transition-shadow duration-300"
              variants={iconVariants}
              whileHover={{
                scale: 1.15,
                rotate: 3,
                transition: { type: "spring", stiffness: 400, damping: 10 }
              }}
            >
              <Image
                src={image}
                alt={title}
                className="w-full h-full object-contain"
              />
            </motion.div>
          )}

          <motion.div
            className="flex-1 min-w-0"
            variants={contentVariants}
          >
            {title && (
              <motion.h3
                className="text-xl 3xl:text-2xl font-bold text-slate-900 mb-2 group-hover:text-primary-600 transition-colors duration-300"
                whileHover={{ x: 3 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {title}
              </motion.h3>
            )}

            {description && (
              <p className="text-sm 3xl:text-base text-slate-600 leading-relaxed">
                {description}
              </p>
            )}

            {children}
          </motion.div>
        </div>

        {actions && (
          <motion.div
            className="relative mt-5 pt-5 border-t border-slate-100 flex items-center justify-end"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.25 }}
          >
            {actions}
          </motion.div>
        )}
      </motion.div>
    );
  }

  // Testimonial Card - Elegant Design
  if (variant === 'testimonial') {
    return (
      <motion.div
        className={`${cardClasses} relative shadow-lg shadow-slate-200/60 hover:shadow-xl hover:shadow-purple-500/20`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={{
          scale: 1.02,
          y: -6,
          transition: { type: "spring", stiffness: 300, damping: 25 }
        }}
        onClick={onClick}
        {...props}
      >
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-pink-500/5" />

        <div className="relative">
          {icon && (
            <motion.div
              className="mb-4"
              initial={{ opacity: 0, scale: 0, rotate: -45 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ delay: delay, type: "spring", stiffness: 200 }}
            >
              <div className="w-12 h-12 3xl:w-14 3xl:h-14 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
                <Icon name="Quote" size={24} className="text-purple-500 3xl:w-8 3xl:h-8" />
              </div>
            </motion.div>
          )}

          {description && (
            <motion.p
              className="text-slate-700 text-lg 3xl:text-xl leading-relaxed mb-8 italic font-medium"
              variants={contentVariants}
            >
              "{description}"
            </motion.p>
          )}

          <div className="flex items-center space-x-4">
            {image && (
              <motion.div
                className="w-14 h-14 rounded-full overflow-hidden ring-4 ring-purple-500/20 shadow-lg"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: delay + 0.2, type: "spring", stiffness: 200 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <Image
                  src={image}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            )}

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: delay + 0.3 }}
            >
              {title && (
                <h4 className="font-bold text-slate-900 text-lg 3xl:text-xl">
                  {title}
                </h4>
              )}
              {subtitle && (
                <p className="text-sm 3xl:text-base text-slate-600 font-medium">
                  {subtitle}
                </p>
              )}
            </motion.div>
          </div>

          {children}
        </div>
      </motion.div>
    );
  }

  // Feature Card - Clean & Modern
  if (variant === 'feature') {
    return (
      <motion.div
        className={`${cardClasses} relative shadow-md hover:shadow-xl shadow-slate-200/60 hover:shadow-blue-500/20`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={{
          scale: 1.05,
          y: -10,
          transition: { type: "spring", stiffness: 400, damping: 25 }
        }}
        onClick={onClick}
        {...props}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-cyan-500/5 opacity-0 hover:opacity-100 transition-opacity duration-500" />

        <div className="relative">
          {icon && (
            <motion.div
              className="mb-5 inline-flex items-center justify-center w-12 h-12 3xl:w-14 3xl:h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-600 shadow-lg shadow-blue-500/30"
              variants={iconVariants}
              whileHover={{
                scale: 1.2,
                rotate: [0, -10, 10, -10, 0],
                boxShadow: "0 20px 25px -5px rgba(59, 130, 246, 0.5)",
                transition: { duration: 0.5 }
              }}
            >
              <Icon name={icon} size={24} className="text-white 3xl:w-8 3xl:h-8 4xl:w-10 4xl:h-10 5xl:w-12 5xl:h-12 6xl:w-14 6xl:h-14" />
            </motion.div>
          )}

          {title && (
            <motion.h3
              className="text-xl 3xl:text-2xl font-bold text-slate-900 mb-3"
              variants={contentVariants}
            >
              {title}
            </motion.h3>
          )}

          {description && (
            <motion.p
              className="text-slate-600 3xl:text-lg leading-relaxed"
              variants={contentVariants}
            >
              {description}
            </motion.p>
          )}

          {children}

          {actions && (
            <motion.div
              className="mt-6 pt-4 flex items-center space-x-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: delay + 0.2 }}
            >
              {actions}
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  }

  // Interactive Card - Premium Design
  if (variant === 'interactive') {
    return (
      <motion.div
        className={`${cardClasses} relative shadow-lg shadow-slate-200/60 hover:shadow-2xl hover:shadow-indigo-500/20`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={{
          scale: 1.03,
          y: -12,
          transition: {
            type: "spring",
            stiffness: 400,
            damping: 25
          }
        }}
        onClick={onClick}
        {...props}
      >
        {/* Animated gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {image && (
          <div className="relative aspect-video w-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
            <motion.div
              className="w-full h-full"
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              <Image
                src={image}
                alt={title}
                className="w-full h-full object-cover"
              />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          </div>
        )}

        <motion.div
          className="relative p-8 3xl:p-10"
          variants={contentVariants}
        >
          {title && (
            <motion.h3
              className="text-xl 3xl:text-2xl font-bold text-slate-900 mb-3 group-hover:text-indigo-600 transition-colors duration-300"
              whileHover={{ x: 5 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              {title}
            </motion.h3>
          )}

          {description && (
            <p className="text-slate-600 3xl:text-lg 4xl:text-xl 5xl:text-2xl 6xl:text-3xl leading-relaxed mb-6">
              {description}
            </p>
          )}

          {children}

          {actions && (
            <motion.div
              className="mt-6 flex items-center justify-between"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: delay + 0.3 }}
            >
              {actions}
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    );
  }

  // Default Card - Sleek Design
  return (
    <motion.div
      className={`${cardClasses} shadow-md hover:shadow-xl shadow-slate-200/60`}
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      whileHover={onClick ? {
        scale: 1.02,
        y: -6,
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
        transition: { type: "spring", stiffness: 300, damping: 25 }
      } : {}}
      onClick={onClick}
      {...props}
    >
      {image && (
        <div className="w-full overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100">
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.4 }}
          >
            <Image
              src={image}
              alt={title || 'Card image'}
              className="w-full h-auto"
            />
          </motion.div>
        </div>
      )}

      <motion.div
        className="p-8 3xl:p-10"
        variants={contentVariants}
      >
        {title && (
          <motion.h3
            className="text-2xl 3xl:text-3xl font-bold text-slate-900 mb-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: delay + 0.1 }}
          >
            {title}
          </motion.h3>
        )}

        {subtitle && (
          <p className="text-sm 3xl:text-base font-semibold text-primary-600 mb-3 uppercase tracking-wide">
            {subtitle}
          </p>
        )}

        {description && (
          <p className="text-slate-600 3xl:text-lg leading-relaxed mb-6">
            {description}
          </p>
        )}

        {children}
      </motion.div>

      {footer && (
        <motion.div
          className="px-8 py-5 bg-gradient-to-br from-slate-50 to-slate-100 border-t border-slate-100"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: delay + 0.2 }}
        >
          {footer}
        </motion.div>
      )}

      {actions && !footer && (
        <motion.div
          className="px-8 py-5 border-t border-slate-100 flex items-center justify-end space-x-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: delay + 0.2 }}
        >
          {actions}
        </motion.div>
      )}
    </motion.div>
  );
}

export default Card;